import React from 'react';
import { Box, Typography, Divider, Avatar, Stack } from '@mui/material';

const TEAM_LOGOS = {
  'Alabama': 'https://a.espncdn.com/i/teamlogos/ncaa/500/333.png',
  'Ohio State': 'https://a.espncdn.com/i/teamlogos/ncaa/500/194.png',
  'Texas': 'https://a.espncdn.com/i/teamlogos/ncaa/500/251.png',
  'Georgia': 'https://a.espncdn.com/i/teamlogos/ncaa/500/61.png',
  'Michigan': 'https://a.espncdn.com/i/teamlogos/ncaa/500/130.png',
  'Notre Dame': 'https://a.espncdn.com/i/teamlogos/ncaa/500/87.png',
  'Kansas City Chiefs': 'https://a.espncdn.com/i/teamlogos/nfl/500/kc.png',
  'Baltimore Ravens': 'https://a.espncdn.com/i/teamlogos/nfl/500/bal.png',
  'San Francisco 49ers': 'https://a.espncdn.com/i/teamlogos/nfl/500/sf.png',
  'Philadelphia Eagles': 'https://a.espncdn.com/i/teamlogos/nfl/500/phi.png',
  'Buffalo Bills': 'https://a.espncdn.com/i/teamlogos/nfl/500/buf.png',
  'Detroit Lions': 'https://a.espncdn.com/i/teamlogos/nfl/500/det.png',
};

export const COLLEGE_ARTICLES = [
  { category: 'SPRING BALL', title: 'Alabama rolls out tempo-heavy dual-QB package in spring install', teams: ['Alabama'] },
  { category: 'SCHEME WATCH', title: 'Ohio State tests six-man jumbo set for red-zone dominance', teams: ['Ohio State'] },
  { category: 'DEPTH CHART', title: 'Texas solidifies LT but keeps three guards rotating before Orange-White game', teams: ['Texas'] },
  { category: 'RECRUITING', title: 'Georgia lands top-ranked edge rusher to bolster 2026 D-line', teams: ['Georgia'] },
  { category: 'SPRING BALL', title: 'Michigan reveals new motion-heavy spread concepts under first-year OC', teams: ['Michigan'] },
  { category: 'ANALYSIS', title: 'Notre Dame QBs battle in spring as starter job stays wide open', teams: ['Notre Dame'] },
  { category: 'SPRING BALL', title: 'Oregon installs tempo-spread hybrid as new era begins in the Big Ten', teams: [] },
  { category: 'TRANSFERS', title: 'Top transfer portal WR narrows list to four programs this week', teams: [] },
];

export const NFL_ARTICLES = [
  { category: 'FILM ROOM', title: 'Chiefs experiment with late-motion flood concepts to stress bracket coverage', teams: ['Kansas City Chiefs'] },
  { category: 'ANALYTICS', title: 'Ravens detail their simulated pressure tree — how Baltimore disguises every blitz', teams: ['Baltimore Ravens'] },
  { category: 'TACTICAL', title: '49ers prototype two-back looks with Deebo Samuel isolated on safeties', teams: ['San Francisco 49ers'] },
  { category: 'FREE AGENCY', title: 'Eagles expected to re-sign interior lineman ahead of March window', teams: ['Philadelphia Eagles'] },
  { category: 'DRAFT PREP', title: 'Bills target edge rusher with first pick — top prospects who fit their scheme', teams: ['Buffalo Bills'] },
  { category: 'GRADES', title: 'Lions offensive line grades out as best unit in the NFC for 2025 season', teams: ['Detroit Lions'] },
  { category: 'FREE AGENCY', title: 'Top 10 wide receivers hitting the market and where they could land', teams: [] },
  { category: 'ANALYTICS', title: 'Expected Points Added leaders from 2025 — the QBs who truly moved the needle', teams: [] },
];

function getLogosForArticle(teams) {
  return (teams || [])
    .map((t) => ({ team: t, logo: TEAM_LOGOS[t] }))
    .filter((e) => Boolean(e.logo));
}

function SidebarItem({ article, accentColor }) {
  const logos = getLogosForArticle(article.teams);
  return (
    <Box
      component="a"
      href="#"
      sx={{
        display: 'block',
        py: 1.5,
        px: 0,
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        '&:hover .article-title': { color: accentColor },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.4 }}>
        {logos.map(({ team, logo }) => (
          <Avatar
            key={team}
            src={logo}
            alt={team}
            sx={{ width: 18, height: 18, borderRadius: '3px' }}
          />
        ))}
        <Typography
          variant="caption"
          sx={{ color: accentColor, fontWeight: 700, fontSize: '0.62rem', letterSpacing: '0.05em' }}
        >
          {article.category}
        </Typography>
      </Stack>
      <Typography
        className="article-title"
        variant="body2"
        sx={{ fontWeight: 500, fontSize: '0.82rem', lineHeight: 1.4, color: '#d0dbe8', transition: 'color 0.15s' }}
      >
        {article.title}
      </Typography>
    </Box>
  );
}

export function CollegeNewsSidebar() {
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '1rem', mb: 1.5, color: '#fff' }}>
        What&apos;s Hot (College)
      </Typography>
      <Divider sx={{ borderColor: 'rgba(245,166,35,0.4)', mb: 1 }} />
      {COLLEGE_ARTICLES.map((article, idx) => (
        <Box key={idx}>
          <SidebarItem article={article} accentColor="#2ea84b" />
          {idx < COLLEGE_ARTICLES.length - 1 && <Divider sx={{ borderColor: 'rgba(22, 42, 66, 0.65)' }} />}
        </Box>
      ))}
    </Box>
  );
}

export function NFLNewsSidebar() {
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '1rem', mb: 1.5, color: '#fff' }}>
        NFL Intel
      </Typography>
      <Divider sx={{ borderColor: 'rgba(245,166,35,0.4)', mb: 1 }} />
      {NFL_ARTICLES.map((article, idx) => (
        <Box key={idx}>
          <SidebarItem article={article} accentColor="#f5a623" />
          {idx < NFL_ARTICLES.length - 1 && <Divider sx={{ borderColor: 'rgba(22, 42, 66, 0.65)' }} />}
        </Box>
      ))}
      <Divider sx={{ borderColor: 'rgba(245,166,35,0.4)', mt: 2, mb: 2 }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.9rem', mb: 1.5, color: '#fff' }}>
        All 32 NFL Teams
      </Typography>
      {[
        { abbr: 'BUF', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/buf.png' },
        { abbr: 'MIA', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/mia.png' },
        { abbr: 'NE',  logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/ne.png'  },
        { abbr: 'NYJ', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/nyj.png' },
        { abbr: 'BAL', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/bal.png' },
        { abbr: 'CIN', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/cin.png' },
        { abbr: 'CLE', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/cle.png' },
        { abbr: 'PIT', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/pit.png' },
        { abbr: 'HOU', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/hou.png' },
        { abbr: 'IND', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/ind.png' },
        { abbr: 'JAX', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/jax.png' },
        { abbr: 'TEN', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/ten.png' },
        { abbr: 'DEN', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/den.png' },
        { abbr: 'KC',  logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/kc.png'  },
        { abbr: 'LV',  logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/lv.png'  },
        { abbr: 'LAC', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/lac.png' },
        { abbr: 'DAL', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/dal.png' },
        { abbr: 'NYG', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/nyg.png' },
        { abbr: 'PHI', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/phi.png' },
        { abbr: 'WAS', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/wsh.png' },
        { abbr: 'CHI', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/chi.png' },
        { abbr: 'DET', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/det.png' },
        { abbr: 'GB',  logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/gb.png'  },
        { abbr: 'MIN', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/min.png' },
        { abbr: 'ATL', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/atl.png' },
        { abbr: 'CAR', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/car.png' },
        { abbr: 'NO',  logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/no.png'  },
        { abbr: 'TB',  logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/tb.png'  },
        { abbr: 'ARI', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/ari.png' },
        { abbr: 'LAR', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/lar.png' },
        { abbr: 'SEA', logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/sea.png' },
        { abbr: 'SF',  logo: 'https://a.espncdn.com/i/teamlogos/nfl/500/sf.png'  },
      ].reduce((rows, team, i) => {
        if (i % 8 === 0) rows.push([]);
        rows[rows.length - 1].push(team);
        return rows;
      }, []).map((row, ri) => (
        <Stack key={ri} direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
          {row.map(({ abbr, logo }) => (
            <Box
              key={abbr}
              component="a"
              href="#"
              title={abbr}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textDecoration: 'none', '&:hover img': { opacity: 1 } }}
            >
              <Avatar src={logo} alt={abbr} sx={{ width: 24, height: 24, borderRadius: '3px', opacity: 0.75, transition: 'opacity 0.15s' }} />
            </Box>
          ))}
        </Stack>
      ))}
    </Box>
  );
}
